import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Package, Car, FileEdit, Clock, HelpCircle, Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/earningsCalculations';

// Charge type options with icons
const CHARGE_TYPES = [
  { value: 'materials', label: 'Materials', icon: Package, description: 'Supplies or materials used' },
  { value: 'travel', label: 'Travel', icon: Car, description: 'Travel or mileage fees' },
  { value: 'scope_change', label: 'Scope Change', icon: FileEdit, description: 'Additional work requested' },
  { value: 'overtime', label: 'Overtime', icon: Clock, description: 'Extra time charges' },
  { value: 'other', label: 'Other', icon: HelpCircle, description: 'Other charges' }
];

/**
 * AdditionalChargesModal
 *
 * Modal for adding supplementary charges (materials, travel, scope changes)
 * on top of the original job charge. Creates separate line items.
 */
export default function AdditionalChargesModal({
  instance,
  job,
  existingCharges = [],
  companyGstRate = 10,
  onClose,
  onAddCharge,
  onUpdateCharge,
  onDeleteCharge,
  loading = false
}) {
  const [charges, setCharges] = useState(existingCharges);
  const [newCharge, setNewCharge] = useState({
    charge_type: 'materials',
    description: '',
    amount: '',
    include_gst: false
  });
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    setCharges(existingCharges);
  }, [existingCharges]);

  const calculateGst = (amount, includeGst) => {
    if (!includeGst) return 0;
    return parseFloat(amount) * (companyGstRate / 100);
  };

  const handleAddCharge = async () => {
    if (!newCharge.description.trim() || !newCharge.amount) return;

    setSubmitting(true);
    try {
      const gstAmount = calculateGst(newCharge.amount, newCharge.include_gst);
      await onAddCharge({
        charge_type: newCharge.charge_type,
        description: newCharge.description,
        amount: parseFloat(newCharge.amount),
        include_gst: newCharge.include_gst,
        gst_amount: gstAmount
      });
      setNewCharge({
        charge_type: 'materials',
        description: '',
        amount: '',
        include_gst: false
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCharge = async (charge) => {
    setSubmitting(true);
    try {
      const gstAmount = calculateGst(charge.amount, charge.include_gst);
      await onUpdateCharge(charge.id, {
        charge_type: charge.charge_type,
        description: charge.description,
        amount: parseFloat(charge.amount),
        include_gst: charge.include_gst,
        gst_amount: gstAmount
      });
      setEditingId(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    setDeleting(chargeId);
    try {
      await onDeleteCharge(chargeId);
    } finally {
      setDeleting(null);
    }
  };

  const totalAdditional = charges.reduce((sum, c) => {
    const amount = parseFloat(c.amount) || 0;
    const gst = parseFloat(c.gst_amount) || 0;
    return sum + amount + gst;
  }, 0);

  const getTypeIcon = (type) => {
    const typeConfig = CHARGE_TYPES.find(t => t.value === type);
    return typeConfig?.icon || HelpCircle;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-background w-full max-w-2xl rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Plus className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Additional Charges</h2>
              <p className="text-text-secondary text-sm">Add materials, travel, or other charges</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Job Info */}
          <div className="mb-4 p-3 bg-surface rounded-lg">
            <p className="text-text-primary font-medium">{job?.job_name}</p>
            <p className="text-text-secondary text-sm">
              {instance?.contractor?.full_name || 'Unassigned'} • {instance?.scheduled_date}
            </p>
          </div>

          {/* Existing Charges */}
          {charges.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-text-secondary mb-3">Existing Additional Charges</h3>
              <div className="space-y-2">
                {charges.map((charge) => {
                  const TypeIcon = getTypeIcon(charge.charge_type);
                  const isEditing = editingId === charge.id;

                  return (
                    <div
                      key={charge.id}
                      className={cn(
                        'p-3 border rounded-lg',
                        isEditing ? 'border-primary bg-primary/5' : 'border-border bg-surface'
                      )}
                    >
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <select
                              value={charge.charge_type}
                              onChange={(e) => setCharges(prev => prev.map(c =>
                                c.id === charge.id ? { ...c, charge_type: e.target.value } : c
                              ))}
                              className="px-3 py-2 bg-background border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary"
                            >
                              {CHARGE_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary text-sm">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={charge.amount}
                                onChange={(e) => setCharges(prev => prev.map(c =>
                                  c.id === charge.id ? { ...c, amount: e.target.value } : c
                                ))}
                                className="w-full pl-7 pr-3 py-2 bg-background border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary"
                              />
                            </div>
                          </div>
                          <input
                            type="text"
                            value={charge.description}
                            onChange={(e) => setCharges(prev => prev.map(c =>
                              c.id === charge.id ? { ...c, description: e.target.value } : c
                            ))}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary"
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={charge.include_gst}
                                onChange={(e) => setCharges(prev => prev.map(c =>
                                  c.id === charge.id ? { ...c, include_gst: e.target.checked } : c
                                ))}
                                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent"
                              />
                              <span className="text-xs text-text-secondary">Add GST ({companyGstRate}%)</span>
                            </label>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setEditingId(null)}
                                className="px-3 py-1.5 text-xs bg-border hover:bg-surface-light text-text-primary rounded transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUpdateCharge(charge)}
                                disabled={submitting}
                                className="px-3 py-1.5 text-xs bg-primary hover:bg-primary-600 text-white rounded transition-colors disabled:opacity-50"
                              >
                                {submitting ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <TypeIcon className="w-4 h-4 text-text-secondary" />
                            <div>
                              <p className="text-sm text-text-primary">{charge.description}</p>
                              <p className="text-xs text-text-tertiary capitalize">{charge.charge_type.replace('_', ' ')}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm text-text-primary">{formatCurrency(charge.amount)}</p>
                              {charge.include_gst && (
                                <p className="text-xs text-text-tertiary">+{formatCurrency(charge.gst_amount)} GST</p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => setEditingId(charge.id)}
                                className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded transition-colors"
                              >
                                <FileEdit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCharge(charge.id)}
                                disabled={deleting === charge.id}
                                className="p-1.5 text-text-secondary hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                              >
                                {deleting === charge.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add New Charge */}
          <div className="border border-dashed border-border rounded-lg p-4">
            <h3 className="text-sm font-medium text-text-secondary mb-3">Add New Charge</h3>

            {/* Charge Type */}
            <div className="mb-3">
              <label className="block text-xs text-text-tertiary mb-1">Type</label>
              <div className="flex flex-wrap gap-2">
                {CHARGE_TYPES.map(type => {
                  const TypeIcon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setNewCharge(prev => ({ ...prev, charge_type: type.value }))}
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors',
                        newCharge.charge_type === type.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-surface text-text-secondary hover:border-border-light'
                      )}
                    >
                      <TypeIcon className="w-3.5 h-3.5" />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description & Amount */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="col-span-2">
                <label className="block text-xs text-text-tertiary mb-1">Description</label>
                <input
                  type="text"
                  value={newCharge.description}
                  onChange={(e) => setNewCharge(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Cleaning supplies"
                  className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary text-sm placeholder-text-tertiary focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary text-sm">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newCharge.amount}
                    onChange={(e) => setNewCharge(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full pl-7 pr-3 py-2 bg-surface border border-border rounded-lg text-text-primary text-sm placeholder-text-tertiary focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
            </div>

            {/* GST Toggle & Add Button */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newCharge.include_gst}
                  onChange={(e) => setNewCharge(prev => ({ ...prev, include_gst: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent"
                />
                <span className="text-xs text-text-secondary">Add GST ({companyGstRate}%)</span>
                {newCharge.include_gst && newCharge.amount && (
                  <span className="text-xs text-text-tertiary">
                    (+{formatCurrency(calculateGst(newCharge.amount, true))})
                  </span>
                )}
              </label>
              <button
                type="button"
                onClick={handleAddCharge}
                disabled={!newCharge.description.trim() || !newCharge.amount || submitting}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
              >
                <Plus size={16} />
                {submitting ? 'Adding...' : 'Add Charge'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-surface flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-text-tertiary">Total Additional Charges</p>
              <p className="text-lg font-bold text-text-primary">{formatCurrency(totalAdditional)}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
